import { useState, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Upload, Check, X, FileText, Star } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '../../lib/api'

interface Scan {
  id: number
  scan_type: string
  subject: string | null
  title: string | null
  quality_score: number | null
  grade_detected: number | null
  xp_awarded: number
  created_at: string
}

interface ScanResult {
  scan_id: number
  xp_awarded: number
  xp_breakdown: [string, number][]
  scans_today: number
  multiplier: number
  duplicate?: boolean
}

const SCAN_TYPES = [
  { value: 'homework', label: 'Homework' },
  { value: 'test', label: 'Test' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'classwork', label: 'Classwork' },
  { value: 'other', label: 'Other' },
]

const SUBJECTS = [
  'Math',
  'Reading',
  'Writing',
  'Science',
  'Social Studies',
  'Spanish',
  'Art',
  'Music',
  'PE',
  'Other',
]

export function ScanWorkflow() {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [showModal, setShowModal] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [scanType, setScanType] = useState('homework')
  const [subject, setSubject] = useState('')
  const [title, setTitle] = useState('')
  const [gradeDetected, setGradeDetected] = useState<string>('')
  const [lastResult, setLastResult] = useState<ScanResult | null>(null)

  // Get today's scans
  const { data: scansData } = useQuery<{ scans: Scan[]; count: number }>({
    queryKey: ['todays-scans'],
    queryFn: async () => {
      const response = await api.get('/api/student/scans/today')
      return response.data
    },
    refetchInterval: 60000, // Refresh every minute
  })

  // Submit scan mutation
  const submitScanMutation = useMutation({
    mutationFn: async (data: {
      scan_type: string
      subject?: string
      title?: string
      image_data?: string
      grade_detected?: number
    }) => {
      const response = await api.post('/api/student/scan', data)
      return response.data as ScanResult
    },
    onSuccess: (data) => {
      setLastResult(data)
      queryClient.invalidateQueries({ queryKey: ['todays-scans'] })
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
      // Reset form after short delay
      setTimeout(() => {
        setSelectedImage(null)
        setScanType('homework')
        setSubject('')
        setTitle('')
        setGradeDetected('')
        setShowModal(false)
      }, 3000)
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const base64 = event.target?.result as string
        setSelectedImage(base64)
        setShowModal(true)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleSubmit = () => {
    if (!selectedImage) return

    // Extract base64 data (remove data:image/xxx;base64, prefix)
    const base64Data = selectedImage.split(',')[1]

    submitScanMutation.mutate({
      scan_type: scanType,
      subject: subject || undefined,
      title: title || undefined,
      image_data: base64Data,
      grade_detected: gradeDetected ? parseFloat(gradeDetected) : undefined,
    })
  }

  const todaysScans = scansData?.scans || []
  const totalXpFromScans = todaysScans.reduce((sum, scan) => sum + scan.xp_awarded, 0)

  return (
    <>
      {/* Scan button and today's summary */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Camera className="w-5 h-5 text-quest-primary" />
              Scan Your Work
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Take a photo of homework to earn XP
            </p>
          </div>
          <button
            onClick={handleCameraCapture}
            className="btn btn-primary flex items-center gap-2"
          >
            <Camera className="w-4 h-4" />
            Scan
          </button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileSelect}
        />

        {/* Today's scans */}
        {todaysScans.length > 0 && (
          <div className="border-t border-gray-700/50 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Today's Scans</span>
              <span className="text-sm text-quest-accent">+{totalXpFromScans} XP</span>
            </div>
            <div className="space-y-2">
              {todaysScans.map((scan) => (
                <div
                  key={scan.id}
                  className="flex items-center justify-between bg-surface-card/50 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">
                      {scan.title || scan.subject || scan.scan_type}
                    </span>
                    {scan.grade_detected && (
                      <span className="text-xs bg-quest-primary/20 text-quest-primary px-2 py-0.5 rounded">
                        {scan.grade_detected}%
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-quest-accent">+{scan.xp_awarded} XP</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {todaysScans.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No scans yet today</p>
            <p className="text-xs">Scan your first homework for +8 XP!</p>
          </div>
        )}
      </div>

      {/* Scan Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-surface-card rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              {/* Success state */}
              {lastResult && !lastResult.duplicate && (
                <div className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <Check className="w-8 h-8 text-green-500" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-2">Scan Complete!</h3>
                  <div className="space-y-1 mb-4">
                    {lastResult.xp_breakdown.map(([reason, xp], index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-400">{reason}</span>
                        <span className="text-quest-accent">+{xp} XP</span>
                      </div>
                    ))}
                    <div className="border-t border-gray-700/50 pt-1 flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-quest-accent">+{lastResult.xp_awarded} XP</span>
                    </div>
                  </div>
                  {lastResult.multiplier > 1 && (
                    <div className="flex items-center justify-center gap-1 text-sm text-quest-accent">
                      <Star className="w-4 h-4" />
                      <span>{lastResult.multiplier}x streak bonus applied!</span>
                    </div>
                  )}
                </div>
              )}

              {/* Duplicate warning */}
              {lastResult?.duplicate && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8 text-yellow-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Already Scanned</h3>
                  <p className="text-gray-400">This document was already scanned.</p>
                </div>
              )}

              {/* Form state */}
              {!lastResult && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">New Scan</h3>
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Image preview */}
                  {selectedImage && (
                    <div className="mb-4 rounded-lg overflow-hidden">
                      <img
                        src={selectedImage}
                        alt="Scan preview"
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Type selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Type
                      </label>
                      <select
                        value={scanType}
                        onChange={(e) => setScanType(e.target.value)}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      >
                        {SCAN_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Subject
                      </label>
                      <select
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      >
                        <option value="">Select subject...</option>
                        {SUBJECTS.map((subj) => (
                          <option key={subj} value={subj}>
                            {subj}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Title (optional)
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g., Chapter 5 Worksheet"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>

                    {/* Grade (if visible) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Grade % (if shown)
                      </label>
                      <input
                        type="number"
                        value={gradeDetected}
                        onChange={(e) => setGradeDetected(e.target.value)}
                        placeholder="e.g., 85"
                        min="0"
                        max="100"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>

                    {/* XP preview */}
                    <div className="bg-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Base XP</span>
                        <span className="text-quest-accent">
                          +{gradeDetected ? 5 : 3} XP
                        </span>
                      </div>
                      {todaysScans.length === 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">First scan bonus</span>
                          <span className="text-quest-accent">+5 XP</span>
                        </div>
                      )}
                    </div>

                    {/* Submit button */}
                    <button
                      onClick={handleSubmit}
                      disabled={submitScanMutation.isPending}
                      className="w-full btn btn-primary flex items-center justify-center gap-2"
                    >
                      {submitScanMutation.isPending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Submit Scan
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
